import type { CityConfig } from "../city-template/types";

export const moscowConfig: CityConfig = {
  city: "Moscow",
  state: "Idaho",
  stateAbbr: "ID",
  slug: "moscow",
  route: "/cities/moscow",
  title: "Moscow ID Passport — Expedited & Emergency Service | Fast Passport Center",
  metaDescription: "Expedited and emergency passport services in Moscow, Idaho — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Moscow, University of Idaho, Pullman, and all Palouse region communities.",
  h1: "Expedited Passport Services in Moscow, ID",
  heroSubtitle: "University of Idaho study abroad program departing for Europe this semester? WSU collaboration taking you to an international conference? Palouse family heading to Canada for a weekend getaway? Moscow residents — from UI Vandals to WSU Cougars across the border to Latah County families — trust us for fast, reliable passport processing. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20view%20of%20Moscow%20Idaho%20with%20the%20University%20of%20Idaho%20campus%20and%20iconic%20Administration%20Building%20spire%20against%20rolling%20Palouse%20hills%20at%20sunset%2C%20golden%20wheat%20fields%20stretching%20to%20the%20horizon%2C%20historic%20downtown%20with%20tree-lined%20Main%20Street%2C%20warm%20autumn%20light%20over%20the%20picturesque%20college%20town%20landscape%20in%20northern%20Idaho&width=1800&height=700&seq=moscow-hero-2026&orientation=landscape",
  heroImageAlt: "Moscow Idaho University of Idaho campus with Palouse hills at sunset",
  geoPosition: "46.7324;-117.0002",
  geoPlacename: "Moscow, Idaho",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle, WA 98101",
    distance: "Closest regional agency — approximately 300 miles via US-195 and I-90, appointments required",
  },
  nearbyCitiesZips: [
    { name: "Moscow", zips: "83843, 83844" },
    { name: "Pullman", zips: "99163, 99164" },
    { name: "Lewiston", zips: "83501" },
    { name: "Colfax", zips: "99111" },
    { name: "Genesee", zips: "83832" },
    { name: "Troy", zips: "83871" },
    { name: "Potlatch", zips: "83855" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Palouse Emergency Processing", desc: "Reserved courier access — as fast as 24 hours for Moscow and Palouse region travelers with immediate international departures from Pullman-Moscow or Spokane airports." },
    { icon: "ri-flight-takeoff-line", title: "PUW & GEG Airport Expertise", desc: "Pullman-Moscow Regional Airport is minutes away, and Spokane International Airport is 90 minutes north. We know both airports and their international connection dynamics." },
    { icon: "ri-graduation-cap-line", title: "UI & WSU Student Focus", desc: "University of Idaho and Washington State University students studying abroad, attending international conferences, and pursuing global research — we understand academic passport timelines intimately." },
    { icon: "ri-home-heart-line", title: "Palouse Region Family Specialists", desc: "From first-time passports for Moscow kids to renewals for Pullman and Colfax families — we make the process clear, fast, and stress-free for the entire Palouse." },
  ],
  internalLinks: [
    { name: "Fast passport services in Lewiston, ID", route: "/cities/lewiston" },
    { name: "Expedited services in Coeur d'Alene, ID", route: "/cities/coeur-dalene" },
    { name: "Idaho passport services overview", route: "/states/idaho" },
  ],
  fpcOfficeAddress: "915 2nd Ave Suite 500, Seattle, WA 98101",
  fpcOfficeNote: "Fast Passport Center serves Moscow via secure registered courier with direct access to the Seattle Passport Agency. Our registered couriers hand-deliver your documents — meaning Moscow residents get faster results without driving 300 miles to Seattle. Ship from any UPS Store, FedEx Office, or post office in Latah County.",
  fpcOfficeDistance: "Serving Moscow, Pullman, Lewiston, Colfax, Genesee, Troy, Potlatch, and all Palouse region communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Moscow and the greater Palouse region. From the University of Idaho campus to historic downtown Main Street, from Pullman across the state line to the rural communities of Latah and Whitman counties — we serve the entire bi-state Palouse region. Our courier service eliminates the need to drive to Seattle for passport agency appointments.",
  nearbyCitiesIntro: "We serve Moscow and all Palouse communities including Pullman, Lewiston, Colfax, Genesee, Troy, and Potlatch. We also serve travelers departing from Pullman-Moscow Regional Airport (PUW), Lewiston-Nez Perce County Airport (LWS), and Spokane International Airport (GEG).",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Moscow as simple as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Moscow travelers start online and ship documents overnight — no office visit required.",
  mistakesIntro: "Palouse travelers encounter unique passport issues — from UI study abroad acceptance letters arriving just weeks before departure to cross-border confusion between Idaho and Washington documentation requirements. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Moscow and Palouse travelers — from UI students to Pullman families to Lewiston professionals — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Moscow, ID? Here are the most common ones we hear from Palouse region travelers.",
  statePageRoute: "/states/idaho",
  statePageName: "Idaho",
  servicesIntro: "Whether you need a renewal before a GEG international connection, an emergency passport for a last-minute academic conference, or a first-time passport for your UI student heading abroad — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://itd.idaho.gov/dmv/",
  dmvLabel: "Idaho Transportation Department — DMV Services",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Closest agency is Seattle (300 miles)", "Not practical for most Palouse travelers"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "No need to drive to Seattle"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent", "Updates only by physical mail", "Cannot upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "International flight from Spokane in two days? Last-minute conference in Canada this week? Our emergency courier service submits via reserved agency slots — as fast as 24 hours for Moscow travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Moscow residents renew fast — no agency visit required. Perfect for UI faculty and Palouse professionals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide UI students and Palouse residents through every step with clear instructions.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: "Student Essential",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your research trip or study abroad is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions for Palouse families traveling abroad together.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Convenient for frequent Canada travelers from northern Idaho.",
      bullets: ["Wallet-size convenience", "Land and sea travel", "Same expedited processing"],
      badge: "Canada Ready",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Moscow ID", description: "Urgent passport processing for Moscow Idaho travelers with immediate departure needs from Spokane or Pullman-Moscow airports." },
    { name: "Expedited Passport Renewal Moscow ID", description: "Fast passport renewal for Moscow Idaho residents with a previously-issued passport." },
    { name: "First-Time Passport Moscow ID", description: "New passport application services for Moscow Idaho residents and University of Idaho students." },
    { name: "Child Passport Moscow ID", description: "Minor passport services for Moscow Idaho families with children under 16." },
    { name: "Name Change Passport Moscow ID", description: "Passport name correction after marriage or legal name changes for Moscow residents." },
    { name: "Passport Card Moscow ID", description: "Passport card services for land and sea crossings including Canada travel from Moscow Idaho." },
  ],
  testimonials: [
    {
      name: "Dr. Katherine W.",
      location: "Moscow — UI Faculty",
      rating: 5,
      text: "My research paper was accepted to an international conference in Vienna with only 3 weeks notice and my passport was expired. Fast Passport Center expedited the renewal in 4 business days. Made my presentation.",
      service: "Passport Renewal",
    },
    {
      name: "Jake R.",
      location: "Moscow — UI Student",
      rating: 5,
      text: "Got accepted to a semester abroad in New Zealand with 6 weeks before departure. Never had a passport. Fast Passport Center guided me through everything and I got it in 5 business days.",
      service: "New Passport",
    },
    {
      name: "The Nguyen Family",
      location: "Pullman, WA",
      rating: 5,
      text: "Booked a trip to Vietnam to visit relatives and realized the kids' passports were expired. Fast Passport Center processed all four applications in 8 days. The document review was incredibly thorough.",
      service: "Family Passport Renewal",
    },
    {
      name: "Sarah L.",
      location: "Moscow",
      rating: 5,
      text: "Needed a passport card for our regular trips to British Columbia. Fast Passport Center processed it quickly and the whole process was seamless from our Palouse home.",
      service: "Passport Card",
    },
  ],
  facilities: [
    {
      id: "moscow-1",
      name: "Moscow Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "220 E 5th St",
      city: "Moscow",
      state: "ID",
      zip: "83843",
      phone: "(208) 882-2301",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Downtown Moscow — walkable from UI campus",
      website: "https://tools.usps.com/find-location.htm?location=1377548",
      websiteLabel: "USPS Location",
      notes: "Primary passport acceptance facility in Moscow. Passport photos available. Very busy during UI semester transitions — book 2+ weeks ahead during August and December.",
      icon: "ri-mail-line",
    },
    {
      id: "moscow-2",
      name: "Latah County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "522 S Adams St",
      city: "Moscow",
      state: "ID",
      zip: "83843",
      phone: "(208) 883-2249",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      distance: "Downtown Moscow — near the courthouse",
      website: "https://www.latah.id.us/clerk/",
      websiteLabel: "Latah County Clerk",
      notes: "County clerk passport acceptance services. Serves all Latah County residents. No photo services on-site — bring your own photos. Often less crowded than the post office.",
      icon: "ri-government-line",
    },
    {
      id: "moscow-3",
      name: "Pullman Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "245 SE Paradise St",
      city: "Pullman",
      state: "WA",
      zip: "99163",
      phone: "(509) 334-2525",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Pullman — 8 miles west (across state line)",
      website: "https://tools.usps.com/find-location.htm?location=1377620",
      websiteLabel: "USPS Location",
      notes: "Serves Pullman and Whitman County. Convenient for WSU students and Pullman residents. Passport photos available. Washington state services — note different state requirements.",
      icon: "ri-mail-line",
    },
    {
      id: "moscow-4",
      name: "Lewiston Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1613 Idaho St",
      city: "Lewiston",
      state: "ID",
      zip: "83501",
      phone: "(208) 746-3666",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Lewiston — 30 miles south",
      website: "https://tools.usps.com/find-location.htm?location=1377545",
      websiteLabel: "USPS Location",
      notes: "Larger acceptance facility in Lewiston serving the Lewis-Clark Valley. Passport photos available. Good backup option if Moscow/Pullman appointments are full.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — Seattle agency is 300 miles away", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive 300 miles to Seattle or fly there", fpc: "Most Moscow travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel with proof of departure", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Palouse & UI/WSU Expertise", agency: "Generic — no PUW/GEG airport or university knowledge", fpc: "Full Palouse region, UI/WSU, and border-area expertise built in" },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "UI Study Abroad Timeline Compression",
      description: "University of Idaho students accepted to study abroad programs often receive notification just 6–8 weeks before departure. First-time applicants need both an acceptance facility visit and processing time — delaying even two weeks can be critical.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Cross-Border Confusion with Washington",
      description: "Moscow is just 8 miles from the Washington border. Palouse residents sometimes confuse Idaho and Washington DMV and vital records requirements. Birth certificates, marriage licenses, and other documents must be from the correct state — we help clarify interstate documentation issues.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Issues at Local Retailers",
      description: "Palouse-area pharmacies and retailers produce photos that frequently fail State Department standards. During UI semester rush, high volumes mean quality slips. We catch these issues before they cause rejection and delay.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Palouse families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Assuming Canada Doesn't Need Full Passport",
      description: "Northern Idaho residents are just a few hours from British Columbia. Many assume a driver's license or enhanced ID works for Canada — it does not for air travel, and land crossing rules have tightened. A passport card is the minimum for land crossings.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or dorm." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Moscow clients ship from the UPS Store on Pullman Rd or the Moscow Post Office." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive 300 miles to Seattle." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Moscow?", a: "We offer Moscow-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Is there a passport office in Moscow?", a: "The Moscow Post Office at 220 E 5th St serves as a passport acceptance facility for first-time applications. The Latah County Clerk at 522 S Adams St also provides acceptance services. For expedited processing beyond the standard timeline, our registered courier service is the fastest option." },
    { q: "Can UI students apply for passports on campus?", a: "The University of Idaho does not have an on-campus passport acceptance facility. UI students should use the Moscow Post Office (walkable from campus) or the Latah County Clerk for their acceptance visit. We provide step-by-step guidance for UI students." },
    { q: "Do you serve Pullman and WSU students too?", a: "Yes. We serve the entire bi-state Palouse region including Pullman, Washington. WSU students and Pullman residents use the Pullman Post Office for acceptance services and then ship documents to us. The process works the same regardless of which state you live in." },
    { q: "Where is the closest passport agency to Moscow?", a: "The Seattle Passport Agency is approximately 300 miles away. It requires appointments and serves life-or-death emergencies only. For all other expedited passport needs, our courier channel is faster and eliminates the drive." },
    { q: "Can you help with flights from Pullman-Moscow Airport?", a: "Yes. Pullman-Moscow Regional Airport (PUW) is just 5 miles from Moscow with Alaska Airlines flights to Seattle and Boise. Spokane International Airport (GEG) is 90 minutes north with broader connections. We regularly assist Palouse travelers with passports for both airports." },
    { q: "What documents do Moscow applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. For Pullman/Washington residents, we provide Washington-specific guidance for vital records. We provide a Moscow-specific checklist." },
    { q: "Where can I get passport photos in Moscow?", a: "The Moscow Post Office, Walmart in Pullman, and the UPS Store on Pullman Rd all offer passport photo services. We recommend confirming photo standards compliance before visiting." },
    { q: "What is the best price for expedited service in Moscow?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers." },
    { q: "How do I check my Moscow passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Moscow to final return shipping typically matches your chosen speed tier." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Moscow Residents",
    intro: "Moscow is a unique bi-state college town — home to the University of Idaho and adjacent to Washington State University in Pullman. This creates international travel demand that far exceeds what a town of 25,000 would normally generate. Here's what Palouse travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PUW — Pullman-Moscow Regional Airport",
        subtitle: "Regional airport just 5 miles from downtown Moscow",
        description: "Pullman-Moscow Regional Airport offers daily Alaska Airlines flights to Seattle and Boise, connecting the Palouse to international gateways worldwide. The 5-minute drive from Moscow makes international travel accessible, but the airport's limited schedule means travelers must plan connections carefully. GEG (Spokane) is 90 minutes away for broader options.",
        tip: "PUW flights to Seattle (SEA) are the most common international gateway for Moscow travelers. Your passport must be valid at PUW check-in for international connections, not just at SeaTac.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "UI & WSU International Programs",
        subtitle: "Two major universities driving significant international travel in a small bi-state region",
        description: "The University of Idaho and Washington State University together enroll over 30,000 students within 8 miles of each other. Both universities send students to study abroad programs, faculty to international conferences, and researchers to global field sites. UI's international programs span agriculture (the university's historic strength), engineering, natural resources, and international studies. WSU adds veterinary medicine, wine science, and communications to the mix.",
        tip: "UI and WSU students should begin their passport process before applying to study abroad programs. Many programs notify students of acceptance only 6–8 weeks before departure — not enough time if you are starting from scratch.",
      },
      {
        icon: "ri-ship-line",
        title: "Canada & Cross-Border Travel Patterns",
        subtitle: "British Columbia is just a few hours north of the Palouse",
        description: "Moscow is roughly 4 hours from the Canadian border via US-95, making British Columbia a popular destination for Palouse families — weekend getaways to the Okanagan Valley, ski trips to interior BC resorts, and summer vacations in the Canadian Rockies. The proximity to Canada means passport cards are a popular option for land crossings.",
        tip: "Passport cards are a cost-effective option for frequent Canada travelers. They are valid for land and sea crossings to Canada, Mexico, and the Caribbean — but NOT for air travel. Choose based on your travel patterns.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Moscow & the Bi-State Palouse",
      description: "Moscow is one of the few American cities that truly functions as a bi-state community with Pullman, Washington. This means our clients include both Idaho and Washington residents, which affects everything from vital records sourcing to DMV visits. We navigate both state systems so you don't have to.",
    },
  },
  destinations: {
    title: "Where Moscow Travelers Go — And Why Passports Get Urgent",
    intro: "The Palouse generates international travel demand far above its population size, driven by two research universities and proximity to Canada.",
    stats: [
      { stat: "45%", label: "of Moscow expedited requests are education-related — UI/WSU students, faculty, researchers" },
      { stat: "6–8 Weeks", label: "average time between study abroad acceptance and international departure" },
      { stat: "Aug & Jan", label: "peak months when semester transitions and international program starts overlap" },
      { stat: "5 Days", label: "typical turnaround for Moscow-area expedited passport processing" },
    ],
    destinations: [
      { name: "Europe", context: "UI/WSU study abroad in UK, France, Italy, Spain, Germany; faculty conferences and research collaborations", urgency: "Semester abroad acceptance to departure often just 6–8 weeks", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "British Columbia getaways, Okanagan wine country, Banff and Jasper national parks, Vancouver business", urgency: "Weekend and short-notice trips — families discover expired documents at packing time", icon: "ri-map-pin-2-line" },
      { name: "Asia & Pacific", context: "UI/WSU exchange programs in Japan, South Korea, China; ag research in New Zealand and Australia", urgency: "Visa processing adds 2–4 weeks to already-tight academic timelines", icon: "ri-map-pin-2-line" },
      { name: "Latin America", context: "UI agriculture research in Central and South America, WSU veterinary programs in Peru and Chile", urgency: "Research and service trips with fixed departure dates and funding deadlines", icon: "ri-map-pin-2-line" },
      { name: "Africa", context: "UI natural resources and conservation research, international development programs", urgency: "Additional vaccination and visa requirements compound passport timeline pressure", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Northern Idaho Service Areas",
    crossLinksDesc: "We serve travelers across northern Idaho and the Palouse with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Lewiston", route: "/cities/lewiston" },
      { name: "Coeur d'Alene", route: "/cities/coeur-dalene" },
    ],
  },
  photoLocations: [
    { name: "Moscow Post Office", address: "220 E 5th St, Moscow, ID 83843", hours: "Mon–Fri 9 AM – 4 PM" },
    { name: "Walmart — Pullman", address: "1690 SE Harvest Dr, Pullman, WA 99163", hours: "Daily 6 AM – 11 PM" },
    { name: "The UPS Store — Moscow", address: "1420 S Blaine St, Moscow, ID 83843", hours: "Mon–Fri 8 AM – 6 PM, Sat 9 AM – 2 PM" },
    { name: "Pullman Post Office", address: "245 SE Paradise St, Pullman, WA 99163", hours: "Mon–Fri 9 AM – 4 PM" },
  ],
  dmvLocations: [
    {
      name: "Latah County DMV — Moscow",
      address: "522 S Adams St, Moscow, ID 83843",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, vehicle registration. Appointment recommended during UI semester transitions.",
      featured: true,
    },
    {
      name: "Whitman County DMV — Colfax (WA residents)",
      address: "310 N Main St, Colfax, WA 99111",
      hours: "Mon–Fri 8:00 AM – 4:30 PM",
      note: "For Pullman and Whitman County, WA residents. REAL ID available. Different state requirements — we guide you through both.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PUW",
      name: "Pullman-Moscow Regional Airport",
      distance: "About 5 minutes from downtown Moscow",
      address: "3200 Airport Complex N, Pullman, WA 99163",
      note: "Regional airport with daily Alaska Airlines flights to Seattle (SEA) and Boise (BOI). Primary gateway for Palouse international connections.",
      flightUrl: "https://www.flypuw.com/",
      flightLabel: "PUW Airport Website",
      featured: true,
    },
    {
      code: "GEG",
      name: "Spokane International Airport",
      distance: "About 90 minutes from Moscow",
      address: "9000 W Airport Dr, Spokane, WA 99224",
      note: "Larger airport with connections to Seattle, Denver, Salt Lake City, Phoenix, Minneapolis, and more. Broader international gateway options.",
      flightUrl: "https://www.spokaneairports.net/",
      flightLabel: "GEG Airport Website",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate (ID)",
      desc: "Request certified birth certificates from Idaho Vital Records. Online, mail, or in-person options available for Latah County.",
      url: "https://healthandwelfare.idaho.gov/services-programs/birth-death-marriage-divorce",
    },
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate (WA)",
      desc: "For Pullman/Whitman County residents — request certified birth certificates from Washington State Department of Health.",
      url: "https://doh.wa.gov/licenses-permits-and-certificates/vital-records",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate (ID)",
      desc: "Request marriage license records from the Latah County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://www.latah.id.us/clerk/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree (ID)",
      desc: "Obtain certified divorce records through the Latah County District Court. Required for name change passport corrections.",
      url: "https://www.latah.id.us/courts/",
    },
  ],
};